/**
 * Storage adapter for supabase-js that implements "Remember me" (ADR 0020).
 *
 * The session (`sb-<ref>-auth-token`, and the optional `-user` companion) is written to
 * localStorage when the user wants to stay signed in, or to sessionStorage when not — then it is
 * gone once the browser (tab) is closed. Everything else, notably the PKCE code verifier, always
 * lives in localStorage so an e-mailed sign-in link opened in a new tab still works.
 *
 * The mode is chosen right before a sign-in call. After a reload the adapter infers it from where
 * the session was found, so token refreshes stay in the same place.
 *
 * E-mailed links open in a NEW tab that knows nothing about the choice. So each PKCE verifier is
 * stored with a small marker holding the mode chosen when the e-mail was requested; when that tab
 * exchanges the link (supabase-js removes the verifier right before saving the session), the
 * adapter adopts the marker's mode.
 */
export type AuthPersistence = "local" | "session";

/** Minimal synchronous Web Storage surface (window.localStorage / sessionStorage, or a fake in tests). */
export type KeyValueStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const SESSION_KEY = /-auth-token(-user)?$/;

export function isSessionKey(key: string): boolean {
  return SESSION_KEY.test(key);
}

/** A PKCE verifier slot (`-flow-<id>-code-verifier`) or the legacy fixed key, not the flow index. */
export function isVerifierKey(key: string): boolean {
  return /-code-verifier$/.test(key) && !/-flows-code-verifier$/.test(key);
}

/** localStorage key of the remember marker stored next to a verifier. */
export const rememberMarkerKey = (verifierKey: string) => `${verifierKey}::fts-persistence`;

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    // Storage may be blocked (private mode, disabled cookies); act as if empty.
    return fallback;
  }
}

export class SwitchableAuthStorage {
  private mode: AuthPersistence = "local";
  /** True once setRemember() was called: an explicit choice wins over inference. */
  private chosen = false;

  constructor(
    private readonly local: KeyValueStorage | null,
    private readonly session: KeyValueStorage | null,
  ) {}

  /** Browser defaults; null stores when unavailable (SSR, tests without DOM). */
  static fromWindow(): SwitchableAuthStorage {
    const get = (name: "localStorage" | "sessionStorage"): KeyValueStorage | null =>
      safe(() => (typeof window !== "undefined" ? window[name] : null), null);
    return new SwitchableAuthStorage(get("localStorage"), get("sessionStorage"));
  }

  get persistence(): AuthPersistence {
    return this.mode;
  }

  /** Call before signing in: true = keep the session after the browser closes. */
  setRemember(remember: boolean): void {
    this.mode = remember ? "local" : "session";
    this.chosen = true;
  }

  private target(key: string): KeyValueStorage | null {
    if (!isSessionKey(key)) return this.local;
    return this.mode === "session" ? (this.session ?? this.local) : this.local;
  }

  getItem(key: string): string | null {
    if (!isSessionKey(key)) return safe(() => this.local?.getItem(key) ?? null, null);
    const fromSession = safe(() => this.session?.getItem(key) ?? null, null);
    if (fromSession !== null) {
      // A tab-only session survives reloads; keep refreshing it in sessionStorage.
      if (!this.chosen) this.mode = "session";
      return fromSession;
    }
    return safe(() => this.local?.getItem(key) ?? null, null);
  }

  setItem(key: string, value: string): void {
    const target = this.target(key);
    safe(() => target?.setItem(key, value), undefined);
    if (isVerifierKey(key)) safe(() => this.local?.setItem(rememberMarkerKey(key), this.mode), undefined);
    if (isSessionKey(key)) {
      // Never leave a copy in the other store (e.g. a remembered session after an unchecked sign-in).
      const other = target === this.local ? this.session : this.local;
      if (other && other !== target) safe(() => other.removeItem(key), undefined);
    }
  }

  removeItem(key: string): void {
    if (isVerifierKey(key)) {
      const marker = safe(() => this.local?.getItem(rememberMarkerKey(key)) ?? null, null);
      safe(() => this.local?.removeItem(rememberMarkerKey(key)), undefined);
      // A tab opened from an e-mail link: follow the choice made when the e-mail was requested.
      if (!this.chosen && (marker === "local" || marker === "session")) {
        this.mode = marker;
        this.chosen = true;
      }
    }
    safe(() => this.local?.removeItem(key), undefined);
    if (isSessionKey(key)) safe(() => this.session?.removeItem(key), undefined);
  }
}
